import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PlanReviewHistoryList } from "@/components/plan_review/PlanReviewHistoryList";
import { SubmissionsDashboard } from "@/components/plan_review/SubmissionsDashboard";
import { ChecklistManager } from "@/components/plan_review/ChecklistManager";
import { PlanReviewChecklistResultCard } from "@/components/plan_review/PlanReviewChecklistResultCard";
import { PlanReviewPilotBanner } from "@/components/plan_review/PlanReviewPilotBanner";
import { PlanReviewZoningSection } from "@/components/plan_review/PlanReviewZoningSection";
import { SubmissionApprovalsCard } from "@/components/plan_review/SubmissionApprovalsCard";
import { PlanReviewResultsSummary } from "@/components/plan_review/PlanReviewResultsSummary";
import { useMunicipality } from "@/context/MunicipalityContext";
import { formatMunicipalityLabel } from "@/lib/municipalityLabel";
import { usePlanReview } from "@/context/PlanReviewContext";
import {
  type PlanReviewBoundingBox,
  type PlanReviewChecklistCategory,
  type PlanReviewCorrectionReason,
  PlanReviewConnectionError,
  createPlanReviewRun,
  deletePlanReviewItemFeedback,
  getPlanReview,
  listPlanReviewChecklists,
  listPlanReviewPlanTypes,
  type PlanReviewPlanType,
  type PlanReviewListItem,
  type PlanReviewDimensionalFact,
  type PlanReviewChecklist,
  type PlanReviewChecklistResult,
  type PlanReviewEvidence,
  type PlanReviewItemFeedback,
  type PlanReviewResults,
  type PlanReviewRun,
  runPlanReviewOrdinanceCompliance,
  savePlanReviewItemFeedback,
} from "@/api/plan_review";
import {
  normalizeHighlightBbox,
  PlanReviewPdfViewer,
  type PlanReviewPageImage,
} from "@/components/plan_review/PlanReviewPdfViewer";
import {
  applicableVisualPages,
  boxesFromVisualIntelligence,
  visualItemProgress,
} from "@/components/plan_review/visualIntelligenceBoxes";
import { findReviewsStartedBy } from "@/components/plan_review/planReviewStartRecovery";
import {
  collapseDuplicateVisionEvidence,
  firstEvidencePage,
  formatElapsedTime,
  humanizeChecklistStatus,
  humanizeRunStatus,
  statusPillClass,
} from "@/components/plan_review/planReviewDisplay";
import { ModuleWorkspaceHeader } from "@/components/module_summary/ModuleWorkspaceHeader";
import { ModuleWorkspaceBody } from "@/components/module_summary/ModuleWorkspaceBody";
import { moduleWorkspaceShellClass } from "@/components/module_summary/moduleWorkspaceLayout";
import type { ModuleSummaryStat } from "@/components/module_summary/ModuleSummaryStrip";
import { formatRelativeTime } from "@/components/module_summary/formatRelativeTime";
import { truncateModuleLabel } from "@/components/module_summary/truncateModuleLabel";
import { Loader2 } from "lucide-react";
import AiDisclaimer from "@/components/AiDisclaimer";
import { cn } from "@/lib/utils";

const STEP_TITLES = {
  1: "Choose Checklist",
  2: "Upload Plan",
  3: "Review & Results",
  4: "Zoning Code",
} as const;

const PLAN_REVIEW_WIZARD_STEPS = [
  { label: STEP_TITLES[1] },
  { label: STEP_TITLES[2] },
  { label: STEP_TITLES[3] },
  { label: STEP_TITLES[4] },
] as const;

const PREPARING_REVIEW_LABEL = "Preparing review";
const STALE_ELAPSED_SECONDS = 600; // 10 min — show "may be stuck" message
const PLAN_REVIEW_IN_FLIGHT_POLL_MS = 1000;
// After a dropped create response, how many times to look for the run the
// server made and how long to wait between looks. The server may still be
// uploading a large plan to S3 when the browser gives up on the request.
const DROPPED_START_LOOKUPS = 3;
const DROPPED_START_RETRY_MS = 2000;
// How many same-titled runs one look will fetch to compare plan file names.
const DROPPED_START_CANDIDATES_PER_LOOKUP = 3;
const PLAN_TYPE_OPTIONS: Array<{
  value: PlanReviewChecklistCategory;
  label: string;
  description: string;
}> = [
  {
    value: "residential_site_plan",
    label: "Residential Site Plan",
    description: "Setbacks, building layout, parking, and other site-plan details.",
  },
  {
    value: "lot_split",
    label: "Lot Split",
    description: "Parcel split reviews focused on lot lines, bearings, lot area, and new parcel boundaries.",
  },
  {
    value: "lot_combination",
    label: "Lot Combination",
    description: "Parcel merge reviews focused on combined parcel geometry and lot-line removal.",
  },
];

type Step = 1 | 2 | 3 | 4;
type SummaryDialogSection = "checklist_items" | "extracted_facts" | "detected_issues" | null;
const CORRECTION_REASON_OPTIONS: Array<{ value: PlanReviewCorrectionReason; label: string }> = [
  { value: "ocr_miss", label: "OCR miss" },
  { value: "visual_only", label: "Visual-only item" },
  { value: "wrong_page", label: "Wrong page" },
  { value: "wrong_bbox", label: "Wrong highlight box" },
  { value: "bad_alias", label: "Bad alias or search term" },
  { value: "model_false_positive", label: "Model false positive" },
];

function isPlanReviewChecklistCategory(value: string | null | undefined): value is PlanReviewChecklistCategory {
  return PLAN_TYPE_OPTIONS.some((option) => option.value === value);
}

function planTypeOption(category: string | null | undefined) {
  return PLAN_TYPE_OPTIONS.find((option) => option.value === category) || PLAN_TYPE_OPTIONS[0];
}

function factFocusKey(fact: PlanReviewDimensionalFact, index: number) {
  if (fact.id) return `fact:${fact.id}`;

  return `fact:${fact.page || "unknown"}:${fact.label || "dimension"}:${fact.value || "value"}:${fact.unit || "unit"}:${index}`;
}

function normalizeViewerFocusKey(rawFocusKey: string | undefined, matchingItemKey: string | undefined, fallback: string) {
  if (rawFocusKey && rawFocusKey.startsWith("item:")) return rawFocusKey;
  if (matchingItemKey) return `item:${matchingItemKey}`;
  return fallback;
}

function boundingBoxesFromResults(
  checklistResults: PlanReviewChecklistResult[],
  dimensionalFacts: PlanReviewDimensionalFact[],
  rawBoundingBoxes: PlanReviewBoundingBox[]
) {
  const checklistBoxes = (Array.isArray(rawBoundingBoxes) ? rawBoundingBoxes : []).flatMap((box, index) => {
    if (!Array.isArray(box.bbox) || box.bbox.length !== 4) return [];
    const matchingItem = checklistResults.find((item) => item.label === box.label || item.key === box.label);
    return [
      {
        ...box,
        bbox: normalizeHighlightBbox(box.bbox),
        focusKey: normalizeViewerFocusKey(
          box.focusKey,
          matchingItem?.key,
          `raw:${box.label || index}`
        ),
      },
    ];
  });

  const evidenceBoxes = checklistResults.flatMap((item) => {
    const evidenceEntries = collapseDuplicateVisionEvidence(Array.isArray(item.evidence) ? item.evidence : []);

    return evidenceEntries.flatMap((evidence, evidenceIndex) => {
      if (typeof evidence.page !== "number") return [];

      const focusKey = `item:${item.key}:ev:${evidenceIndex}`;
      const pageLinked = evidence.page_link_only === true || evidence.localized === false;

      if (pageLinked) return [];

      if (!Array.isArray(evidence.bbox)) return [];

      return [
        {
          page: evidence.page,
          bbox: normalizeHighlightBbox(evidence.bbox),
          confidence: evidence.confidence,
          label: item.label,
          focusKey,
        },
      ];
    });
  });

  const factBoxes = dimensionalFacts.flatMap((fact, index) => {
    if (!Array.isArray(fact.bbox)) return [];

    return [
      {
        page: fact.page,
        bbox: normalizeHighlightBbox(fact.bbox),
        confidence: fact.confidence,
        label: fact.label || "Detected dimension",
        focusKey: factFocusKey(fact, index),
      },
    ];
  });

  const combined = [...checklistBoxes, ...evidenceBoxes, ...factBoxes];

  // Dedupe by geometry so the same rect doesn't get rendered twice (e.g. checklist boxes +
  // evidence boxes). When collisions happen, keep the evidence (:ev:) focusKey so citation
  // highlighting still works.
  const dedupedByGeometry = new Map<string, (typeof combined)[number]>();

  for (const box of combined) {
    const page = box.page;
    const bbox = box.bbox;

    if (typeof page !== "number" || !Array.isArray(bbox) || bbox.length !== 4) {
      // Keep "non-standard" boxes unique by their focus key/label.
      const key = JSON.stringify([box.focusKey || box.label || "", page ?? null]);
      if (!dedupedByGeometry.has(key)) dedupedByGeometry.set(key, box);
      continue;
    }

    const geomKey = `${page}|${bbox.map((v) => (typeof v === "number" ? v.toFixed(4) : String(v))).join(",")}`;
    const existing = dedupedByGeometry.get(geomKey);
    if (!existing) {
      dedupedByGeometry.set(geomKey, box);
      continue;
    }

    const existingIsEvidence = existing.focusKey?.includes(":ev:") ?? false;
    const currentIsEvidence = box.focusKey?.includes(":ev:") ?? false;
    if (!existingIsEvidence && currentIsEvidence) {
      dedupedByGeometry.set(geomKey, box);
    }
  }

  return Array.from(dedupedByGeometry.values());
}

export function PlanReviewAssistant({ embedded = false }: { embedded?: boolean }) {
  const { municipalities, municipality, setMunicipalityId } = useMunicipality();
  const { selectedReviewId, setSelectedReviewId, reviews = [], refreshReviews } = usePlanReview();
  const viewerSectionRef = useRef<HTMLDivElement | null>(null);
  const selectedReviewIdRef = useRef<number | null>(selectedReviewId);
  // Counts Start Review attempts. An attempt that finds the counter has moved
  // on (the planner opened another review, started a new one, or left) stops
  // touching state, so a late create response or a late recovery look cannot
  // land on top of whatever they did next.
  const startAttemptRef = useRef(0);
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [error, setError] = useState<string | null>(null);
  // When embedded, Plan Review's home is the submissions queue; "checklists" is
  // the plan-type/checklist manager tab and "review" is the wizard.
  const [view, setView] = useState<"dashboard" | "checklists" | "review">(embedded ? "dashboard" : "review");

  const [planTypes, setPlanTypes] = useState<PlanReviewPlanType[]>([]);
  const [selectedPlanTypeId, setSelectedPlanTypeId] = useState<number | null>(null);
  const [checklists, setChecklists] = useState<PlanReviewChecklist[]>([]);
  const [selectedChecklistId, setSelectedChecklistId] = useState<number | null>(null);

  const [planFile, setPlanFile] = useState<File | null>(null);
  const [planName, setPlanName] = useState("");
  const [reviewRun, setReviewRun] = useState<PlanReviewRun | null>(null);
  const [loading, setLoading] = useState(false);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [processingElapsedSeconds, setProcessingElapsedSeconds] = useState(0);
  const [editingItem, setEditingItem] = useState<PlanReviewChecklistResult | null>(null);
  const [feedbackStatus, setFeedbackStatus] = useState<"present" | "missing" | "uncertain">("uncertain");
  const [feedbackExplanation, setFeedbackExplanation] = useState("");
  const [feedbackValue, setFeedbackValue] = useState("");
  const [feedbackCorrectionReason, setFeedbackCorrectionReason] = useState<PlanReviewCorrectionReason | "">("");
  const [feedbackNote, setFeedbackNote] = useState("");
  const [savingFeedback, setSavingFeedback] = useState(false);
  const [focusedItemKey, setFocusedItemKey] = useState<string | null>(null);
  /** Off until the user clicks a citation (single index) or map pin (all). */
  const [highlightScope, setHighlightScope] = useState<"off" | "all" | number>("off");
  const [focusedFactKey, setFocusedFactKey] = useState<string | null>(null);
  const [focusedEvidencePage, setFocusedEvidencePage] = useState<number | null>(null);
  const [summaryDialog, setSummaryDialog] = useState<SummaryDialogSection>(null);

  const effectiveResults = useMemo(
    (): PlanReviewResults => reviewRun?.effective_results || reviewRun?.results || {},
    [reviewRun?.effective_results, reviewRun?.results]
  );
  const reviewComplete = reviewRun?.status === "complete";
  const checklistResults = useMemo(
    () => effectiveResults.checklist_results || [],
    [effectiveResults]
  );
  const dimensionalFacts = useMemo(
    () => (effectiveResults.dimensional_facts || []) as PlanReviewDimensionalFact[],
    [effectiveResults]
  );
  const rawBoundingBoxes = useMemo(
    () => (effectiveResults.bounding_boxes || []) as PlanReviewBoundingBox[],
    [effectiveResults]
  );
  const visualPayload = reviewRun?.visual_intelligence;
  const visualInFlight = reviewRun?.visual_intelligence_status === "running";
  const visualPageNumbers = useMemo(() => applicableVisualPages(visualPayload), [visualPayload]);
  const visualLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    (reviewRun?.checklist_snapshot?.items || []).forEach((item) => {
      if (item.key) labels[item.key] = item.label;
    });
    checklistResults.forEach((item) => {
      labels[item.key] = item.label;
    });
    return labels;
  }, [reviewRun?.checklist_snapshot?.items, checklistResults]);
  const visualBoxes = useMemo(
    () => (visualInFlight ? boxesFromVisualIntelligence(visualPayload, visualLabels, true) : []),
    [visualInFlight, visualPayload, visualLabels]
  );
  const viewerBoxes = useMemo(
    () => [...visualBoxes, ...boundingBoxesFromResults(checklistResults, dimensionalFacts, rawBoundingBoxes)],
    [visualBoxes, checklistResults, dimensionalFacts, rawBoundingBoxes]
  );
  const issues = useMemo(() => effectiveResults.issues || [], [effectiveResults]);
  const issueRows = useMemo(() => effectiveResults.issue_rows || [], [effectiveResults]);
  const summary = useMemo(() => effectiveResults.summary, [effectiveResults]);
  const ordinanceCompliance = effectiveResults.ordinance_compliance;
  const ordinanceInFlight = ["pending", "processing"].includes(ordinanceCompliance?.status || "");
  const runInFlight = ["pending", "processing"].includes(reviewRun?.status || "");
  const showPlanReviewWorkspace = !!reviewRun?.id;
  const heartbeatStale =
    runInFlight &&
    reviewRun?.last_heartbeat_at &&
    (Date.now() - new Date(reviewRun.last_heartbeat_at).getTime()) / 1000 >= STALE_ELAPSED_SECONDS;
  const elapsedStale = runInFlight && processingElapsedSeconds >= STALE_ELAPSED_SECONDS;
  const appearsStale = heartbeatStale || (!reviewRun?.last_heartbeat_at && elapsedStale);
  const selectedPlanTypeRecord = useMemo(
    () => planTypes.find((planType) => planType.id === selectedPlanTypeId) ?? null,
    [planTypes, selectedPlanTypeId]
  );
  const reviewPlanType = planTypeOption(reviewRun?.checklist_snapshot?.category);

  useEffect(() => {
    if (municipality?.id) return;
    if (municipalities.length === 1) {
      setMunicipalityId(municipalities[0].id);
    }
  }, [municipalities, municipality?.id, setMunicipalityId]);

  useEffect(() => {
    if (!selectedReviewId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const review = await getPlanReview(selectedReviewId);
        if (cancelled) return;
        setReviewRun(review);
        setSelectedChecklistId(review.checklist_id);
        setPlanName(review.title || "");
        setCurrentStep(3);
      } catch {
        if (cancelled) return;
        setError("Failed to load review");
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [selectedReviewId]);

  useEffect(() => {
    if (selectedReviewId != null) setView("review");
  }, [selectedReviewId]);

  useEffect(() => {
    const loadPlanTypes = async () => {
      if (!municipality?.id) {
        setPlanTypes([]);
        setSelectedPlanTypeId(null);
        return;
      }
      try {
        const list = await listPlanReviewPlanTypes({ municipalityId: municipality.id });
        setPlanTypes(list);
        setSelectedPlanTypeId((currentId) => {
          if (currentId && list.some((planType) => planType.id === currentId)) return currentId;
          return list[0]?.id ?? null;
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load plan types");
      }
    };
    void loadPlanTypes();
  }, [municipality?.id, view]);

  // Once plan types arrive (or a review loads), align the wizard's plan type
  // with the open review's snapshot category.
  useEffect(() => {
    const snapshotCategory = reviewRun?.checklist_snapshot?.category;
    if (!snapshotCategory || !isPlanReviewChecklistCategory(snapshotCategory)) return;
    setSelectedPlanTypeId((currentId) => {
      const current = planTypes.find((planType) => planType.id === currentId);
      if (current?.review_kind === snapshotCategory) return currentId;
      return planTypes.find((planType) => planType.review_kind === snapshotCategory)?.id ?? currentId;
    });
  }, [reviewRun?.checklist_snapshot?.category, planTypes]);

  useEffect(() => {
    selectedReviewIdRef.current = selectedReviewId;
  }, [selectedReviewId]);

  useEffect(() => {
    abandonStartAttempt();
  }, [selectedReviewId]);

  useEffect(() => {
    return () => {
      startAttemptRef.current += 1;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadChecklists = async () => {
      if (!municipality?.id || !selectedPlanTypeId) {
        setChecklists([]);
        setSelectedChecklistId(null);
        return;
      }

      try {
        const list = await listPlanReviewChecklists({
          municipalityId: municipality.id,
          planTypeId: selectedPlanTypeId,
        });
        if (cancelled) return;
        setChecklists(list);
        setSelectedChecklistId((currentId) => {
          if (currentId && list.some((checklist) => checklist.id === currentId)) return currentId;
          // An open review keeps its own checklist id (it may be archived and
          // absent from the picker list); don't clobber it with a fallback.
          if (selectedReviewIdRef.current != null) return currentId;
          return list[0]?.id ?? null;
        });
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load checklists");
      }
    };
    void loadChecklists();
    return () => {
      cancelled = true;
    };
    // view: a checklist confirmed in the Checklists tab must be pickable
    // when the planner comes back to the wizard.
  }, [municipality?.id, selectedPlanTypeId, view]);

  useEffect(() => {
    if (!reviewRun?.id) return;
    if (!runInFlight && !ordinanceInFlight && !visualInFlight) return;

    const tick = async () => {
      try {
        const latest = await getPlanReview(reviewRun.id);
        setReviewRun(latest);
        if (runInFlight && !["pending", "processing"].includes(latest.status)) {
          void refreshReviews();
        }
      } catch {
        // Keep polling; transient errors are expected in long-running jobs.
      }
    };

    void tick();

    const pollInterval = window.setInterval(tick, PLAN_REVIEW_IN_FLIGHT_POLL_MS);

    return () => {
      window.clearInterval(pollInterval);
    };
  }, [reviewRun?.id, runInFlight, ordinanceInFlight, visualInFlight, refreshReviews]);

  useEffect(() => {
    setFocusedItemKey(null);
    setHighlightScope("off");
    setFocusedFactKey(null);
    setFocusedEvidencePage(null);
  }, [reviewRun?.id]);

  useEffect(() => {
    if (!reviewRun?.id || !runInFlight) {
      setProcessingElapsedSeconds(0);
      return;
    }

    setProcessingElapsedSeconds(0);
    const startedAt = Date.now();

    const elapsedInterval = window.setInterval(() => {
      const elapsedSeconds = Math.floor((Date.now() - startedAt) / 1000);
      setProcessingElapsedSeconds(elapsedSeconds);
    }, 1000);

    return () => {
      window.clearInterval(elapsedInterval);
    };
  }, [reviewRun?.id, runInFlight]);

  useEffect(() => {
    if (!editingItem) return;

    const existingFeedback = reviewRun?.item_feedbacks.find((feedback) => feedback.item_key === editingItem.key);
    const nextStatus = existingFeedback?.corrected_status || editingItem.status;
    setFeedbackStatus(nextStatus === "pending" ? "uncertain" : nextStatus);
    setFeedbackExplanation(existingFeedback?.corrected_explanation || editingItem.explanation || editingItem.reason || "");
    setFeedbackValue(existingFeedback?.corrected_value || String(editingItem.extracted_value || ""));
    setFeedbackCorrectionReason(existingFeedback?.correction_reason || editingItem.correction_reason || "");
    setFeedbackNote(existingFeedback?.note || "");
  }, [editingItem, reviewRun?.item_feedbacks]);

  useEffect(() => {
    if (checklistResults.length === 0) {
      setFocusedItemKey(null);
      setHighlightScope("off");
      setFocusedEvidencePage(null);
      return;
    }

    if (!focusedItemKey) return;

    const focusedItemStillExists = checklistResults.some((item) => item.key === focusedItemKey);
    if (focusedItemStillExists) return;

    setFocusedItemKey(null);
    setHighlightScope("off");
    setFocusedEvidencePage(null);
  }, [checklistResults, focusedItemKey]);

  // Ends whatever Start Review attempt is in flight and frees the button.
  function abandonStartAttempt() {
    startAttemptRef.current += 1;
    setLoading(false);
  }

  const openStartedReview = (review: PlanReviewRun) => {
    abandonStartAttempt();
    setReviewRun(review);
    setSelectedReviewId(review.id);
    setCurrentStep(3);
  };

  // The browser can drop the create response after the server has already
  // queued the run (Heroku H27). Refresh the list a few times, and if the run
  // this submit made shows up, open it exactly as a successful create would
  // have. The refresh also puts it in the sidebar, which is where the message
  // below sends the planner when nothing matched. The Start Review button
  // stays disabled throughout (loading is still true), and the attempt stops
  // silently if the planner moves on before it finishes.
  const recoverDroppedStart = async (
    submit: { title: string; fileName: string; municipalityId: number; submittedAt: number },
    stillCurrent: () => boolean
  ) => {
    for (let lookup = 1; lookup <= DROPPED_START_LOOKUPS; lookup++) {
      if (lookup > 1) {
        await new Promise((resolve) => window.setTimeout(resolve, DROPPED_START_RETRY_MS));
      }
      if (!stillCurrent()) return;
      // refreshReviews never throws (it resolves [] on failure), so a bad
      // connection just means an empty look here.
      const candidates = findReviewsStartedBy(await refreshReviews(), submit).slice(
        0,
        DROPPED_START_CANDIDATES_PER_LOOKUP
      );
      for (const candidate of candidates) {
        if (!stillCurrent()) return;
        let review: PlanReviewRun;
        try {
          review = await getPlanReview(candidate.id);
        } catch {
          // One candidate failing to load must not hide the others.
          continue;
        }
        if (!stillCurrent()) return;
        if (review.source_plan_filename === submit.fileName) {
          openStartedReview(review);
          return;
        }
      }
    }
    if (!stillCurrent()) return;
    setError(
      `The connection dropped before the server confirmed this review. It may have started anyway, so check the sidebar for "${submit.title}" before starting it again.`
    );
  };

  const handleStartReview = async () => {
    if (!municipality?.id || !selectedChecklistId || !planFile) return;
    const trimmedName = planName.trim();
    if (!trimmedName) return;
    startAttemptRef.current += 1;
    const attempt = startAttemptRef.current;
    const stillCurrent = () => startAttemptRef.current === attempt;
    setError(null);
    setLoading(true);
    const submittedAt = Date.now();
    try {
      const review = await createPlanReviewRun({
        municipalityId: municipality.id,
        checklistId: selectedChecklistId,
        file: planFile,
        title: trimmedName,
      });
      // The sidebar should show the run even when the planner has moved on.
      void refreshReviews();
      if (stillCurrent()) openStartedReview(review);
    } catch (e) {
      if (e instanceof PlanReviewConnectionError) {
        await recoverDroppedStart(
          { title: trimmedName, fileName: planFile.name, municipalityId: municipality.id, submittedAt },
          stillCurrent
        );
      } else if (stillCurrent()) {
        setError(e instanceof Error ? e.message : "Failed to start review");
      }
    } finally {
      if (stillCurrent()) setLoading(false);
    }
  };

  const handleRunOrdinanceCompliance = async (params: { documentIds: number[]; desiredZoning: string }) => {
    if (!reviewRun?.id) return;
    setError(null);
    setComplianceLoading(true);
    try {
      const updated = await runPlanReviewOrdinanceCompliance(reviewRun.id, {
        document_ids: params.documentIds,
        desired_zoning: params.desiredZoning,
      });
      setReviewRun(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to start zoning code check");
    } finally {
      setComplianceLoading(false);
    }
  };

  const resetNewReview = () => {
    abandonStartAttempt();
    setCurrentStep(1);
    setPlanFile(null);
    setPlanName("");
    setReviewRun(null);
    setSelectedReviewId(null);
  };

  const handleNewReviewFromQueue = () => {
    resetNewReview();
    setView("review");
  };

  const handleOpenReviewFromQueue = (planReviewId: number) => {
    setSelectedReviewId(planReviewId);
    setView("review");
  };

  const handleBackToQueue = () => {
    resetNewReview();
    setView("dashboard");
  };

  const canContinueFromStep1 = !!selectedChecklistId;
  const canContinueFromStep2 = !!selectedChecklistId && !!planFile && !!planName.trim();
  const viewerPdfUrls = useMemo(
    () => [reviewRun?.viewer_plan_url, reviewRun?.source_plan_url, reviewRun?.annotated_plan_url].filter(
      (url): url is string => !!url
    ),
    [reviewRun?.viewer_plan_url, reviewRun?.source_plan_url, reviewRun?.annotated_plan_url]
  );
  const viewerPdfUrl = viewerPdfUrls[0] || null;
  const viewerPageImages = useMemo(() => {
    const pages = visualPayload?.pages;
    const images: Record<string, PlanReviewPageImage> = {};
    if (!pages) return images;

    for (const [number, page] of Object.entries(pages)) {
      if (!page?.viewer_url || typeof page.width !== "number" || typeof page.height !== "number") continue;
      images[number] = {
        url: page.viewer_url,
        width: page.width,
        height: page.height,
        crop: page.crop,
      };
    }
    return images;
  }, [visualPayload]);
  const completedRunTimeSeconds = useMemo(() => {
    if (reviewRun?.status !== "complete") return null;

    const totalMs =
      reviewRun?.duration_ms ??
      reviewRun?.results?.stage_timings?.total_ms ??
      reviewRun?.effective_results?.stage_timings?.total_ms;
    if (typeof totalMs === "number" && Number.isFinite(totalMs) && totalMs >= 0) {
      return Math.round(totalMs / 1000);
    }

    return null;
  }, [
    reviewRun?.duration_ms,
    reviewRun?.effective_results?.stage_timings?.total_ms,
    reviewRun?.results?.stage_timings?.total_ms,
    reviewRun?.status,
  ]);
  const completedRunTimeLabel =
    completedRunTimeSeconds !== null ? formatElapsedTime(completedRunTimeSeconds) : null;

  const existingFeedbackForEditingItem: PlanReviewItemFeedback | undefined = editingItem
    ? reviewRun?.item_feedbacks.find((feedback) => feedback.item_key === editingItem.key)
    : undefined;
  const focusedItem = checklistResults.find((item) => item.key === focusedItemKey) || null;
  const focusedFact = dimensionalFacts.find((fact, index) => factFocusKey(fact, index) === focusedFactKey) || null;
  const focusedPage =
    focusedEvidencePage ?? firstEvidencePage(focusedItem) ?? focusedFact?.page ?? visualBoxes[0]?.page ?? null;
  const viewerFocusedKey = useMemo(() => {
    if (focusedFactKey) return focusedFactKey;
    if (!focusedItem || highlightScope === "off") return null;
    if (highlightScope === "all") return `item:${focusedItem.key}`;
    if (typeof highlightScope === "number") {
      const citationKey = `item:${focusedItem.key}:ev:${highlightScope}`;
      const hasCitationBox = viewerBoxes.some((box) => box.focusKey === citationKey);
      return hasCitationBox ? citationKey : `item:${focusedItem.key}`;
    }
    return null;
  }, [focusedFactKey, focusedItem, highlightScope, viewerBoxes]);
  const focusedItemHasHighlight =
    highlightScope !== "off" &&
    !!focusedItem &&
    viewerBoxes.some((box) => {
      if (typeof box.page !== "number" || box.page !== focusedPage) return false;
      const key = viewerFocusedKey || "";
      if (key.includes(":ev:") || key.includes(":vi:")) return box.focusKey === key;
      return box.focusKey === key || box.focusKey?.startsWith(`${key}:`);
    });

  const focusedCitationEvidence =
    typeof highlightScope === "number"
      ? collapseDuplicateVisionEvidence(Array.isArray(focusedItem?.evidence) ? focusedItem.evidence : [])[highlightScope]
      : null;
  const focusedCitationIsPageLinked =
    !!focusedCitationEvidence &&
    (focusedCitationEvidence.page_link_only === true || focusedCitationEvidence.localized === false);

  const viewerUrl = viewerPdfUrl || null;
  const showViewerHighlightHint =
    !!viewerUrl && highlightScope === "off" && !focusedFactKey && visualBoxes.length === 0;

  const scrollViewerIntoView = () => {
    try {
      viewerSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch {
      viewerSectionRef.current?.scrollIntoView();
    }
  };

  const focusItemOnPlan = (
    item: PlanReviewChecklistResult,
    page?: number | null,
    evidenceIndex?: number | null,
    highlightMode: "all" | "citation" | "off" = "off",
  ) => {
    setFocusedItemKey(item.key);
    setFocusedFactKey(null);
    if (highlightMode === "all") {
      setHighlightScope("all");
    } else if (highlightMode === "citation" && typeof evidenceIndex === "number") {
      setHighlightScope(evidenceIndex);
    } else {
      setHighlightScope("off");
    }
    setFocusedEvidencePage(page ?? firstEvidencePage(item));
    setSummaryDialog(null);
    window.requestAnimationFrame(scrollViewerIntoView);
  };

  const focusFactOnPlan = (fact: PlanReviewDimensionalFact, index: number) => {
    setFocusedItemKey(null);
    setHighlightScope("off");
    setFocusedFactKey(factFocusKey(fact, index));
    setFocusedEvidencePage(fact.page || null);
    setSummaryDialog(null);
    window.requestAnimationFrame(scrollViewerIntoView);
  };

  const openEditDialog = (item: PlanReviewChecklistResult) => {
    setEditingItem(item);
  };

  const closeEditDialog = () => {
    setEditingItem(null);
    setFeedbackExplanation("");
    setFeedbackValue("");
    setFeedbackCorrectionReason("");
    setFeedbackNote("");
    setSavingFeedback(false);
  };

  const saveFeedback = async () => {
    if (!reviewRun?.id || !editingItem) return;

    setSavingFeedback(true);
    setError(null);

    try {
      const updatedReview = await savePlanReviewItemFeedback({
        reviewId: reviewRun.id,
        itemKey: editingItem.key,
        correctedStatus: feedbackStatus,
        correctedExplanation: feedbackExplanation,
        correctedValue: feedbackValue || undefined,
        correctedEvidence: (editingItem.evidence || []) as PlanReviewEvidence[],
        correctionReason: feedbackCorrectionReason || undefined,
        note: feedbackNote,
      });
      setReviewRun(updatedReview);
      closeEditDialog();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save checklist edit");
      setSavingFeedback(false);
    }
  };

  const clearFeedback = async () => {
    if (!reviewRun?.id || !existingFeedbackForEditingItem) return;

    setSavingFeedback(true);
    setError(null);

    try {
      const updatedReview = await deletePlanReviewItemFeedback({
        reviewId: reviewRun.id,
        feedbackId: existingFeedbackForEditingItem.id,
      });
      setReviewRun(updatedReview);
      closeEditDialog();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to clear checklist edit");
      setSavingFeedback(false);
    }
  };

  const planReviewSummary = useMemo(() => {
    const inFlight = reviews.filter(
      (review) => review.status === "pending" || review.status === "processing",
    ).length;
    const latest = reviews.reduce<PlanReviewListItem | null>((acc, review) => {
      if (!acc) return review;
      return new Date(review.updated_at) > new Date(acc.updated_at) ? review : acc;
    }, null);

    const stats: ModuleSummaryStat[] = [
      { label: "Saved reviews", value: reviews.length },
      {
        label: "In progress",
        value: inFlight,
        tone: inFlight > 0 ? "warning" : "muted",
      },
      {
        label: "Latest review",
        value: latest ? truncateModuleLabel(latest.title) : "None yet",
        hint: latest ? formatRelativeTime(latest.updated_at) : undefined,
        tone: latest ? "default" : "muted",
      },
    ];

    let insight: string | null = null;
    if (reviews.length === 0) {
      insight =
        "Upload a site plan and checklist to get item-by-item compliance with evidence on the drawing.";
    } else if (inFlight > 0) {
      insight = "A review is still running—open it to watch progress or wait for results to finish.";
    } else if (latest) {
      insight = `Reopen “${truncateModuleLabel(latest.title, 40)}” to continue checklist or ordinance review.`;
    }

    return { stats, insight, latest };
  }, [reviews]);

  const hideModuleSummary = embedded && !!reviewRun && currentStep >= 3;

  return (
    <div
      className={embedded ? "flex flex-1 min-h-0 overflow-hidden" : moduleWorkspaceShellClass}
      data-testid="plan-review-assistant"
    >
      {embedded ? (
        <aside className="ordinal-scrollbar hidden w-64 shrink-0 flex-col overflow-y-auto border-r bg-muted/20 md:flex">
          <div className="p-2">
            <PlanReviewHistoryList />
          </div>
        </aside>
      ) : null}
      <div className={embedded ? "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden" : undefined}>
      {embedded && view !== "review" ? (
        <div className="flex items-center gap-1 border-b px-4 pt-2" role="tablist" aria-label="Plan Review sections">
          {([
            { key: "dashboard", label: "Submissions" },
            { key: "checklists", label: "Checklists" },
          ] as const).map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={view === tab.key}
              onClick={() => setView(tab.key)}
              className={cn(
                "-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors",
                view === tab.key
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      ) : null}
      {embedded && view === "checklists" ? (
        <ChecklistManager municipalityId={municipality?.id ?? null} />
      ) : embedded && view === "dashboard" ? (
        <SubmissionsDashboard
          municipalityId={municipality?.id ?? null}
          onOpenReview={handleOpenReviewFromQueue}
          onNewReview={handleNewReviewFromQueue}
        />
      ) : (
      <>
      <ModuleWorkspaceHeader
        title="Plan Review"
        subtitle={
          embedded
            ? undefined
            : "Upload a checklist and site plan, then review structured compliance results with evidence."
        }
        hideTitle={embedded}
        density={embedded ? "compact" : "default"}
        meta={
          <>
            {reviewRun?.title && (
              <p className="truncate text-sm font-medium text-foreground" title={reviewRun.title}>
                Plan: {reviewRun.title}
              </p>
            )}
            {/* The sidebar lists unlogged runs only, so an open review says
                for itself when it came from a logged submission. The queue
                shows no ids, so name the project a planner can find there. */}
            {reviewRun?.submission_id ? (
              <p className="truncate text-xs text-muted-foreground">
                Logged submission:{" "}
                {reviewRun.submission_project_name?.trim() || `#${reviewRun.submission_id}`}
              </p>
            ) : null}
            {reviewRun?.checklist_snapshot?.category && (
              <p className="text-xs text-muted-foreground">Plan type: {reviewPlanType.label}</p>
            )}
          </>
        }
        actions={
          <div className="flex items-center gap-2">
            {embedded ? (
              <Button className="shrink-0" variant="outline" onClick={handleBackToQueue}>
                Back to queue
              </Button>
            ) : null}
            <Button className="shrink-0" variant="default" onClick={resetNewReview}>
              New Review
            </Button>
          </div>
        }
        summary={
          hideModuleSummary
            ? undefined
            : {
                stats: planReviewSummary.stats,
                action: planReviewSummary.latest
                  ? {
                      label: "Open last review",
                      onClick: () => setSelectedReviewId(planReviewSummary.latest!.id),
                    }
                  : undefined,
                insight: planReviewSummary.insight,
                testId: "plan-review-module-summary",
              }
        }
        wizard={{
          steps: [...PLAN_REVIEW_WIZARD_STEPS],
          currentStep,
          onStepClick: (step) => setCurrentStep(step as Step),
          isStepClickable: (step) => !!reviewRun?.id && (step === 3 || step === 4),
          isStepComplete: (step) => step === 4 && ordinanceCompliance?.status === "complete",
          testId: "plan-review-wizard",
        }}
      />

      <ModuleWorkspaceBody width="wide">
        {!(reviewRun && currentStep >= 3) ? <PlanReviewPilotBanner /> : null}

        {!!error && (
          <Card className="border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </Card>
        )}

        <Card className="p-5 space-y-5">
          {!embedded ? (
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Municipality</label>
            <select
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              value={municipality?.id ?? ""}
              onChange={(event) => setMunicipalityId(Number(event.target.value))}
            >
              <option value="" disabled>
                Select municipality
              </option>
              {municipalities.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {formatMunicipalityLabel(entry)}
                </option>
              ))}
            </select>
          </div>
          ) : null}

          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700" htmlFor="plan-review-plan-type">
                  Plan type
                </label>
                <select
                  id="plan-review-plan-type"
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                  value={selectedPlanTypeId ?? ""}
                  onChange={(event) => {
                    setSelectedPlanTypeId(Number(event.target.value));
                    setSelectedChecklistId(null);
                    setError(null);
                  }}
                >
                  <option value="" disabled>
                    Select plan type
                  </option>
                  {planTypes.map((planType) => (
                    <option key={planType.id} value={planType.id}>
                      {planType.name}
                    </option>
                  ))}
                </select>
                {selectedPlanTypeRecord?.description ? (
                  <p className="text-xs text-slate-500">{selectedPlanTypeRecord.description}</p>
                ) : null}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Use Existing Checklist</label>
                <select
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                  value={selectedChecklistId ?? ""}
                  onChange={(event) => setSelectedChecklistId(Number(event.target.value))}
                >
                  <option value="" disabled>
                    Select checklist
                  </option>
                  {checklists.map((checklist) => (
                    <option key={checklist.id} value={checklist.id}>
                      {checklist.name}
                      {checklist.system_default ? " (Ordinal Default)" : ""}
                    </option>
                  ))}
                </select>
                {checklists.length === 0 && (
                  <p className="text-xs text-slate-500">
                    No checklists are available yet for {selectedPlanTypeRecord?.name ?? "this plan type"}.
                    {embedded ? " Manage them from the Checklists tab." : ""}
                  </p>
                )}
              </div>

              {/* Uploads live in the manager (ENG-2005): the proposal is
                  reviewed and confirmed there, and saving never lands the
                  planner in an unlogged plan upload. */}
              {embedded ? (
                <p className="text-xs text-slate-500" data-testid="checklist-upload-hint">
                  Need a checklist that is not listed?{" "}
                  <button
                    type="button"
                    className="font-medium text-slate-700 underline underline-offset-2 hover:text-slate-900"
                    onClick={() => setView("checklists")}
                  >
                    Upload and confirm it from the Checklists tab
                  </button>
                  , then pick it here.
                </p>
              ) : null}

              <div className="flex justify-end">
                <Button onClick={() => setCurrentStep(2)} disabled={!canContinueFromStep1}>
                  Continue to Upload Plan
                </Button>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="rounded-md border border-slate-200 bg-slate-50/80 p-3">
                <div className="text-sm font-medium text-slate-900">Plan type</div>
                <div className="mt-1 text-sm text-slate-700">{selectedPlanTypeRecord?.name ?? "Not selected"}</div>
                {selectedPlanTypeRecord?.description ? (
                  <p className="mt-1 text-xs text-slate-500">{selectedPlanTypeRecord.description}</p>
                ) : null}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700" htmlFor="plan-review-plan-name">
                  Plan name
                </label>
                <Input
                  id="plan-review-plan-name"
                  type="text"
                  value={planName}
                  onChange={(event) => setPlanName(event.target.value)}
                  placeholder="e.g. 123 Main St site plan"
                />
                <p className="text-xs text-slate-500">Shown in the sidebar and used to identify this review.</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Plan PDF</label>
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={(event) => setPlanFile(event.target.files?.[0] || null)}
                />
                <p className="text-xs text-slate-500">Upload one plan PDF for this review.</p>
              </div>

              <div className="flex justify-between">
                <Button variant="secondary" onClick={() => setCurrentStep(1)}>
                  Back
                </Button>
                <Button onClick={handleStartReview} disabled={!canContinueFromStep2 || loading}>
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Start Review
                </Button>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className={reviewComplete ? "space-y-4" : "space-y-6"}>
              {!(runInFlight || reviewComplete) && (
                <div className="flex items-center gap-3">
                  <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${statusPillClass(reviewRun?.status || "pending")}`}>
                    {humanizeRunStatus(reviewRun?.status || "pending")}
                  </span>
                  {appearsStale && (
                    <span className="inline-flex items-center text-sm text-amber-700">
                      Review may be stuck—it may have been interrupted by a deploy or restart.
                    </span>
                  )}
                </div>
              )}

              {runInFlight && appearsStale && (
                <p className="text-sm text-amber-700">
                  Review may be stuck—it may have been interrupted by a deploy or restart.
                </p>
              )}

              {appearsStale && (
                <Card className="border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <p className="font-medium">This review is taking longer than expected.</p>
                  <p className="mt-1 text-amber-800">
                    It may have been interrupted by a deploy or restart. Try starting a new review.
                  </p>
                  <Button className="mt-3" variant="outline" onClick={resetNewReview}>
                    New Review
                  </Button>
                </Card>
              )}

              {runInFlight && (
                <p className="flex items-center gap-2 text-sm text-slate-700" data-testid="plan-review-preparing">
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin text-blue-700" aria-hidden="true" />
                  {PREPARING_REVIEW_LABEL}
                </p>
              )}

              {reviewRun?.status === "failed" && (
                <Card className="border-red-200 bg-red-50 p-3 text-sm text-red-800">
                  {reviewRun.error_message || "Plan review failed"}
                </Card>
              )}

              {showPlanReviewWorkspace && (
                <div className="space-y-6">
                  {!reviewComplete && (
                    <Card className="border-amber-200 bg-amber-50/80 p-3 text-sm text-amber-950">
                      {runInFlight
                        ? "Results below update as the review progresses. Editing and code compliance unlock when the review finishes."
                        : "Review did not complete. Partial findings may still appear below."}
                    </Card>
                  )}
                  <PlanReviewResultsSummary
                    reviewComplete={reviewComplete && !visualInFlight}
                    summary={summary}
                    checklistResultsLength={checklistResults.length}
                    dimensionalFactsLength={dimensionalFacts.length}
                    issuesLength={issues.length}
                    completedRunTimeLabel={completedRunTimeLabel}
                    onSummaryDialog={setSummaryDialog}
                    onGoToZoning={() => setCurrentStep(4)}
                  />

                  <div className={`grid gap-4 ${viewerUrl ? "lg:grid-cols-3 lg:items-start" : ""}`}>
                    {viewerUrl && (
                      <Card ref={viewerSectionRef} className="min-w-0 overflow-hidden p-3 space-y-2 lg:col-span-2">
                        <div className="flex items-center justify-between gap-3">
                          <h2 className="truncate text-sm font-semibold text-slate-900">
                            {focusedItem?.label ?? focusedFact?.label ?? "Plan viewer"}
                          </h2>
                          {focusedItem ? (
                            <span
                              className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${statusPillClass(focusedItem.status)}`}
                            >
                              {humanizeChecklistStatus(focusedItem.status)}
                            </span>
                          ) : focusedFact ? (
                            <span className="shrink-0 text-xs font-medium text-slate-600">
                              {[focusedFact.value, focusedFact.unit].filter(Boolean).join(" ").trim() || null}
                            </span>
                          ) : null}
                        </div>
                        {showViewerHighlightHint && (
                          <p className="text-xs text-slate-500">
                            Click a citation number to highlight evidence on the drawing.
                          </p>
                        )}
                        {highlightScope !== "off" && focusedItem && focusedPage && !focusedItemHasHighlight && (
                          <p className="text-xs text-slate-500">
                            {focusedCitationIsPageLinked
                              ? "No localized highlight box for this citation; jumped to the page."
                              : "No highlight box on this page for the selected source."}
                          </p>
                        )}
                        <PlanReviewPdfViewer
                          boxes={viewerBoxes}
                          annotatedPlanUrl={reviewRun?.annotated_plan_url}
                          sourcePlanUrl={reviewRun?.source_plan_url}
                          focusedKey={viewerFocusedKey}
                          focusedLabel={highlightScope !== "off" ? focusedItem?.label || null : null}
                          page={focusedPage}
                          pdfUrls={viewerPdfUrls}
                          pageImages={viewerPageImages}
                          onPageChange={(nextPage) => setFocusedEvidencePage(nextPage)}
                        />
                      </Card>
                    )}

                    <div className="flex min-h-0 flex-col gap-6 lg:col-span-1 lg:sticky lg:top-4 lg:max-h-[calc(100vh-5rem)] lg:self-start lg:overflow-y-auto">
                      {reviewRun?.submission_id && reviewRun?.municipality_id ? (
                        <SubmissionApprovalsCard
                          municipalityId={reviewRun.municipality_id}
                          submissionId={reviewRun.submission_id}
                        />
                      ) : null}

                      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden p-4 space-y-3 lg:min-h-[12rem]">
                        <h2 className="text-sm font-semibold text-slate-800 shrink-0">
                          Checklist Results
                          {(!reviewComplete && runInFlight) || visualInFlight ? " (partial)" : ""}
                        </h2>
                        <div
                          className="ordinal-scrollbar max-h-[min(70vh,720px)] overflow-y-auto overscroll-y-contain pr-1 lg:max-h-none lg:min-h-0 lg:flex-1"
                          data-testid="plan-review-checklist-results-scroll"
                          role="region"
                          aria-label="Checklist results"
                        >
                          <div className="space-y-3 pr-2">
                            {checklistResults.length === 0 && (
                              <div className="text-sm text-slate-500">No checklist results were produced.</div>
                            )}
                            {checklistResults.map((item) => (
                              <PlanReviewChecklistResultCard
                                key={item.key}
                                item={item}
                                viewerPdfUrl={viewerPdfUrl}
                                isFocused={focusedItemKey === item.key}
                                activeCitationIndex={
                                  focusedItemKey === item.key && typeof highlightScope === "number"
                                    ? highlightScope
                                    : null
                                }
                                onViewOnPlan={(row, page, evidenceIndex, highlightMode) =>
                                  focusItemOnPlan(row, page, evidenceIndex, highlightMode)
                                }
                                onEdit={(row) => openEditDialog(row)}
                                readOnly={!reviewComplete || visualInFlight}
                                visualProgress={
                                  visualInFlight
                                    ? visualItemProgress(visualPayload, item.key, visualPageNumbers)
                                    : undefined
                                }
                              />
                            ))}
                          </div>
                        </div>
                        <AiDisclaimer className="shrink-0" />
                      </Card>
                    </div>
                  </div>
                </div>
              )}

              {!reviewComplete && (
                <div className="flex justify-start">
                  <Button variant="secondary" onClick={() => setCurrentStep(2)}>
                    Back
                  </Button>
                </div>
              )}
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-6">
              <PlanReviewZoningSection
                reviewRun={reviewRun}
                reviewComplete={reviewComplete}
                runInFlight={runInFlight}
                municipalityId={reviewRun?.municipality_id ?? municipality?.id}
                ordinanceCompliance={ordinanceCompliance}
                complianceLoading={complianceLoading}
                annotatedPlanUrl={reviewRun?.annotated_plan_url}
                sourcePlanUrl={reviewRun?.source_plan_url}
                onRunCompliance={handleRunOrdinanceCompliance}
              />

              <div className="flex items-center justify-between">
                <Button variant="secondary" onClick={() => setCurrentStep(3)}>
                  Back to plan results
                </Button>
              </div>
            </div>
          )}
        </Card>
      </ModuleWorkspaceBody>

      <Dialog open={!!editingItem} onOpenChange={(open) => (!open ? closeEditDialog() : undefined)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Checklist Result</DialogTitle>
            <DialogDescription>
              Save a human correction for this checklist item. The original machine result is preserved separately for training data.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <div className="text-sm font-medium text-slate-900">{editingItem?.label}</div>
              {editingItem?.explanation && <div className="mt-1 text-xs text-slate-600">{editingItem.explanation}</div>}
            </div>

            <div className="space-y-2">
              <label htmlFor="plan-review-feedback-status" className="text-sm font-medium text-slate-700">Status</label>
              <select
                id="plan-review-feedback-status"
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                value={feedbackStatus}
                onChange={(event) => setFeedbackStatus(event.target.value as "present" | "missing" | "uncertain")}
              >
                <option value="present">present</option>
                <option value="uncertain">uncertain</option>
                <option value="missing">missing</option>
              </select>
            </div>

            <div className="space-y-2">
              <label htmlFor="plan-review-feedback-explanation" className="text-sm font-medium text-slate-700">Explanation</label>
              <Textarea
                id="plan-review-feedback-explanation"
                value={feedbackExplanation}
                onChange={(event) => setFeedbackExplanation(event.target.value)}
                placeholder="Explain why this item should have a different result."
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="plan-review-feedback-value" className="text-sm font-medium text-slate-700">Extracted Value</label>
              <Input
                id="plan-review-feedback-value"
                type="text"
                value={feedbackValue}
                onChange={(event) => setFeedbackValue(event.target.value)}
                placeholder="Optional corrected value"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="plan-review-feedback-correction-reason" className="text-sm font-medium text-slate-700">Correction Reason</label>
              <select
                id="plan-review-feedback-correction-reason"
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                value={feedbackCorrectionReason}
                onChange={(event) => setFeedbackCorrectionReason(event.target.value as PlanReviewCorrectionReason | "")}
              >
                <option value="">Select a reason</option>
                {CORRECTION_REASON_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label htmlFor="plan-review-feedback-note" className="text-sm font-medium text-slate-700">Editor Note</label>
              <Textarea
                id="plan-review-feedback-note"
                value={feedbackNote}
                onChange={(event) => setFeedbackNote(event.target.value)}
                placeholder="Optional note for future model training or QA review."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            {existingFeedbackForEditingItem && (
              <Button type="button" variant="outline" onClick={clearFeedback} disabled={savingFeedback}>
                Clear Edit
              </Button>
            )}
            <Button type="button" variant="outline" onClick={closeEditDialog} disabled={savingFeedback}>
              Cancel
            </Button>
            <Button type="button" onClick={saveFeedback} disabled={savingFeedback || !editingItem}>
              {savingFeedback ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={summaryDialog !== null} onOpenChange={(open) => (!open ? setSummaryDialog(null) : undefined)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {summaryDialog === "checklist_items" && "Checklist Items"}
              {summaryDialog === "extracted_facts" && "Extracted Facts"}
              {summaryDialog === "detected_issues" && "Detected Issues"}
            </DialogTitle>
            <DialogDescription>
              {summaryDialog === "checklist_items" && "Review all checklist items and jump directly to evidence on the plan."}
              {summaryDialog === "extracted_facts" && "Inspect the dimensional facts pulled from the plan pages."}
              {summaryDialog === "detected_issues" && "Review required checklist items that are currently missing or uncertain."}
            </DialogDescription>
          </DialogHeader>

          {summaryDialog === "checklist_items" && (
            <div className="ordinal-scrollbar max-h-[65vh] space-y-3 overflow-y-auto">
              {checklistResults.map((item) => (
                <div key={`summary-${item.key}`} className="rounded-md border border-slate-200 p-3 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="text-sm font-medium text-slate-900">{item.label}</div>
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusPillClass(item.status)}`}>
                      {humanizeChecklistStatus(item.status)}
                    </span>
                  </div>
                  {!!item.explanation && <div className="text-xs text-slate-600">{item.explanation}</div>}
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => focusItemOnPlan(item, null, null, "all")}
                      disabled={!viewerUrl || !firstEvidencePage(item)}
                    >
                      Show on drawing
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSummaryDialog(null);
                        openEditDialog(item);
                      }}
                    >
                      Edit
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {summaryDialog === "extracted_facts" && (
            <div className="ordinal-scrollbar max-h-[65vh] space-y-3 overflow-y-auto">
              {dimensionalFacts.length === 0 && <div className="text-sm text-slate-500">No extracted facts were produced.</div>}
              {dimensionalFacts.map((fact, index) => (
                <div key={`${fact.id || fact.label || "fact"}-${index}`} className="rounded-md border border-slate-200 p-3 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-medium text-slate-900">{fact.statement || `${fact.label || "Detected dimension"} detected.`}</div>
                      <div className="text-xs text-slate-500">Page {fact.page || "Unknown"}</div>
                    </div>
                    <div className="text-sm font-medium text-slate-900">
                      {[fact.value, fact.unit].filter(Boolean).join(" ").trim() || "No value"}
                    </div>
                  </div>
                  {!!fact.label && (
                    <div className="text-xs font-medium text-slate-600">
                      Label: {fact.label}
                    </div>
                  )}
                  {!!fact.snippet && fact.snippet !== fact.statement && <div className="text-xs text-slate-600">{fact.snippet}</div>}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => focusFactOnPlan(fact, index)}
                    disabled={!viewerUrl || typeof fact.page !== "number" || !Array.isArray(fact.bbox)}
                    aria-label={`Show ${fact.label || "detected dimension"} on drawing`}
                  >
                    Show on drawing
                  </Button>
                </div>
              ))}
            </div>
          )}

          {summaryDialog === "detected_issues" && (
            <div className="ordinal-scrollbar max-h-[65vh] space-y-3 overflow-y-auto">
              {issueRows.length === 0 && issues.length === 0 && (
                <div className="text-sm text-slate-500">No issues detected for required checklist items.</div>
              )}
              {issueRows.length > 0
                ? issueRows.map((row) => (
                    <div
                      key={`issue-dialog-${row.item_key}`}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 p-3 text-sm"
                    >
                      <span className="font-medium text-slate-900">{row.label}</span>
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusPillClass(row.status)}`}>
                        {humanizeChecklistStatus(row.status)}
                      </span>
                    </div>
                  ))
                : issues.map((issue: string, index: number) => (
                    <div key={`issue-dialog-${index}`} className="rounded-md border border-slate-200 p-3 text-sm text-slate-700">
                      {issue}
                    </div>
                  ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
      </>
      )}
      </div>
    </div>
  );
}
